<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="2" />
    <h4>Reporte de Especialidades por Prestador</h4>
    <hr />

    <!-- Filtros -->
    <div class="container mb-4">
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label fw-bold">Filtrar por Prestador</label>
          <select v-model="filtroPrestador" class="form-select">
            <option value="">-- Todos los prestadores --</option>
            <option
              v-for="p in prestadores"
              :key="p.idPrestador"
              :value="p.idPrestador"
            >
              {{ p.razonSocial }}
            </option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label fw-bold">Filtrar por Especialidad (sistema)</label>
          <select v-model="filtroEspecialidad" class="form-select">
            <option value="">-- Todas las especialidades --</option>
            <option
              v-for="e in especialidades"
              :key="e.idEspecialidad"
              :value="e.idEspecialidad"
            >
              {{ e.nombre }}
            </option>
          </select>
        </div>
        <div class="col-md-2 d-grid">
          <button
            @click="exportarCSV"
            class="btn btn-outline-success"
            :disabled="listaFiltrada.length === 0"
          >
            <i class="material-icons me-1" style="font-size:18px;vertical-align:middle">download</i>
            Exportar CSV
          </button>
        </div>
        <div class="col-md-2 d-grid">
          <button
            @click="exportarPDF"
            class="btn btn-outline-danger"
            :disabled="listaFiltrada.length === 0"
          >
            <i class="material-icons me-1" style="font-size:18px;vertical-align:middle">picture_as_pdf</i>
            Exportar PDF
          </button>
        </div>
      </div>
    </div>

    <!-- Selector registros por página -->
    <div class="d-flex justify-content-end align-items-center gap-2 mb-2" v-if="listaFiltrada.length > 0">
      <label class="form-label mb-0 small fw-bold">Mostrar</label>
      <select
        v-model.number="elementosPorPagina"
        class="form-select form-select-sm"
        style="width:75px"
        @change="aplicarPaginacion(1)"
      >
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
      <span class="small text-muted">registros</span>
    </div>

    <!-- Tabla -->
    <div class="table-responsive" v-if="listaFiltrada.length > 0">
      <table class="table table-hover table-bordered table-sm">
        <thead class="table-dark">
          <tr>
            <th class="text-center">#</th>
            <th class="th-sort" @click="ordenarPor('prestadorRazonSocial')">
              Prestador <span>{{ iconOrden('prestadorRazonSocial') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('nombre')">
              Especialidad (prestador) <span>{{ iconOrden('nombre') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('especialidadNombre')">
              Especialidad (sistema) <span>{{ iconOrden('especialidadNombre') }}</span>
            </th>
            <th class="th-sort text-center" @click="ordenarPor('estado')">
              Estado <span>{{ iconOrden('estado') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in datosPaginados" :key="item.idEspePres">
            <td class="text-center">{{ (paginaActual - 1) * elementosPorPagina + idx + 1 }}</td>
            <td>{{ item.prestadorRazonSocial }}</td>
            <td>{{ item.nombre }}</td>
            <td>{{ item.especialidadNombre }}</td>
            <td class="text-center">
              <span class="badge" :class="item.estado === 1 ? 'bg-success' : 'bg-danger'">
                {{ item.estado === 1 ? 'Activo' : 'Inactivo' }}
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="table-secondary">
            <td colspan="5" class="text-end fw-bold small">
              Mostrando {{ datosPaginados.length }} de {{ listaFiltrada.length }} registros
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Sin resultados -->
    <div v-if="listaFiltrada.length === 0 && lista.length > 0" class="alert alert-warning mt-3" role="alert">
      No se encontraron registros con los filtros aplicados.
    </div>

    <!-- Paginación -->
    <div
      class="d-flex justify-content-between align-items-center mt-3"
      v-if="listaFiltrada.length > 0"
    >
      <span class="small text-muted">
        Página {{ paginaActual }} de {{ totalPaginas() }} —
        {{ listaFiltrada.length }} registros
      </span>
      <nav aria-label="Paginación">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(1)">«</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="getPreviousPage">‹</a>
          </li>
          <li
            v-for="pagina in paginasVisibles"
            :key="pagina"
            class="page-item"
            :class="{ active: pagina === paginaActual, disabled: pagina === '...' }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="pagina !== '...' && aplicarPaginacion(pagina)"
            >
              {{ pagina }}
            </a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
            <a class="page-link" href="#" @click.prevent="getNextPage">›</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(totalPaginas())">»</a>
          </li>
        </ul>
      </nav>
    </div>

    <Footer />
  </div>
</template>

<script>
import api from "@/services/api";
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  components: { Layout, Footer },
  data() {
    return {
      lista:              [],
      prestadores:        [],
      especialidades:     [],
      filtroPrestador:    "",
      filtroEspecialidad: "",
      idEmpresa:          0,
      // Paginación
      elementosPorPagina: 10,
      datosPaginados:     [],
      paginaActual:       1,
      // Ordenamiento
      campoOrden:         "prestadorRazonSocial",
      ascendente:         true,
    };
  },
  computed: {
    listaFiltrada() {
      const campo = this.campoOrden;
      return this.lista
        .filter((item) => {
          const porPrestador    = this.filtroPrestador    ? item.idPrestador    === this.filtroPrestador    : true;
          const porEspecialidad = this.filtroEspecialidad ? item.idEspecialidad === this.filtroEspecialidad : true;
          return porPrestador && porEspecialidad;
        })
        .sort((a, b) => {
          let va = a[campo] ?? "";
          let vb = b[campo] ?? "";
          if (campo === "estado") {
            return this.ascendente ? va - vb : vb - va;
          }
          const cmp = String(va).localeCompare(String(vb), "es");
          return this.ascendente ? cmp : -cmp;
        });
    },
    paginasVisibles() {
      const total  = this.totalPaginas();
      const actual = this.paginaActual;
      const delta  = 2;
      const rango  = [];
      for (
        let i = Math.max(2, actual - delta);
        i <= Math.min(total - 1, actual + delta);
        i++
      ) {
        rango.push(i);
      }
      if (rango.length && rango[0] > 2)                        rango.unshift("...");
      if (rango.length && rango[rango.length - 1] < total - 1) rango.push("...");
      const paginas = [1];
      rango.forEach((p) => paginas.push(p));
      if (total > 1) paginas.push(total);
      return paginas;
    },
  },
  watch: {
    listaFiltrada() {
      this.aplicarPaginacion(1);
    },
  },
  mounted() {
    this.idEmpresa = window.sessionStorage.getItem("idEmpresa");
    this.$refs.layoutRef.toggleProcesos();
    this.$refs.layoutRef.toggleReportes();
    this.$refs.layoutRef.toggleParametros();
    this.$refs.layoutRef.toggleAyuda();
    this.cargarDatos();
  },
  methods: {
    // ── Ordenamiento ───────────────────────────────────────
    ordenarPor(campo) {
      if (this.campoOrden === campo) {
        this.ascendente = !this.ascendente;
      } else {
        this.campoOrden = campo;
        this.ascendente = true;
      }
      this.aplicarPaginacion(1);
    },
    iconOrden(campo) {
      if (this.campoOrden !== campo) return "⇅";
      return this.ascendente ? "↑" : "↓";
    },
    // ── Paginación ─────────────────────────────────────────
    totalPaginas() {
      return Math.max(
        1,
        Math.ceil(this.listaFiltrada.length / this.elementosPorPagina)
      );
    },
    aplicarPaginacion(noPagina) {
      this.paginaActual   = noPagina;
      const ini           = (noPagina - 1) * this.elementosPorPagina;
      this.datosPaginados = this.listaFiltrada.slice(ini, ini + this.elementosPorPagina);
    },
    getPreviousPage() {
      if (this.paginaActual > 1) this.aplicarPaginacion(this.paginaActual - 1);
    },
    getNextPage() {
      if (this.paginaActual < this.totalPaginas())
        this.aplicarPaginacion(this.paginaActual + 1);
    },
    // ── Carga de datos ─────────────────────────────────────
    async cargarDatos() {
      try {
        const [vwEspePresRes, prestadoresRes, especialidadesRes] = await Promise.all([
          api.get(`/vwespepress/idempresa/${this.idEmpresa}`),
          api.get(`/vwprestadors/idempresa/${this.idEmpresa}/servicios`),
          api.get(`/vwespecialidads`),
        ]);
        this.lista          = Array.isArray(vwEspePresRes.data)    ? vwEspePresRes.data    : [];
        this.prestadores    = Array.isArray(prestadoresRes.data)   ? prestadoresRes.data   : [];
        this.especialidades = Array.isArray(especialidadesRes.data) ? especialidadesRes.data : [];
        this.aplicarPaginacion(1);
      } catch (error) {
        console.error("Error cargando los datos:", error);
      }
    },
    // ── Exportar CSV (separador ";") ───────────────────────
    exportarCSV() {
      const escape = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val).replace(/"/g, '""');
        return str.includes(";") || str.includes("\n") ? `"${str}"` : str;
      };
      const headers = [
        "#", "Prestador", "Especialidad (prestador)",
        "Especialidad (sistema)", "Estado",
      ];
      const rows = [
        headers.join(";"),
        ...this.listaFiltrada.map((item, idx) =>
          [
            idx + 1,
            item.prestadorRazonSocial,
            item.nombre,
            item.especialidadNombre,
            item.estado === 1 ? "Activo" : "Inactivo",
          ]
            .map(escape)
            .join(";")
        ),
      ];
      const bom  = "\uFEFF";
      const blob = new Blob([bom + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "reporte_especialidades.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    // ── Exportar PDF ───────────────────────────────────────
    exportarPDF() {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text("Reporte de Especialidades por Prestador", 14, 15);
      autoTable(doc, {
        startY: 20,
        head: [["#", "Prestador", "Especialidad (prestador)", "Especialidad (sistema)", "Estado"]],
        body: this.listaFiltrada.map((item, idx) => [
          idx + 1,
          item.prestadorRazonSocial,
          item.nombre,
          item.especialidadNombre,
          item.estado === 1 ? "Activo" : "Inactivo",
        ]),
        styles:     { fontSize: 9 },
        headStyles: { fillColor: [33, 37, 41] }, // table-dark equivalente
      });
      doc.save("reporte_especialidades.pdf");
    },
  },
};
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover {
  background-color: #3d5166;
}
.table-hover tbody tr {
  cursor: pointer;
}
</style>